import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import { isGuid, toIsoDate } from '@/core/models/practice.models';
import {
  ReportTypeCode,
  type ReportDetail,
  type ReportTypeOption,
  type SaveReportPayload,
} from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { reportOptionsService } from '@/core/services/reportOptions';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import { ClientKind, isUnincorporated, useClientKind } from '@/features/tr-reportsettings/clientKind';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DateField } from '@/kit/DateField';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Radio, RadioGroup } from '@/kit/Radio';
import { Select } from '@/kit/Select';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import {
  balanceSourceOf,
  cancelChanges,
  chooseAdjustedSource,
  chooseFiguresSource,
  fieldError,
  initialFormState,
  isFormInvalid,
  LLP_DIRECTOR_SIGN_DATE_LABEL,
  LLP_DIRECTOR_SIGN_DATE_TITLE,
  setControl,
  missingFieldsMessage,
  ACCOUNTS_TYPE_MESSAGE,
  offeredAdjustedSources,
  refNoMaxLength,
  showInvalid,
  touch,
  touchAll,
  unlockedTrialBalances,
  withLoadedData,
  withSavedReport,
  type FieldName,
  type FormState,
  type FormValues,
  type SourceKind,
} from './reportFormModel';
import { REPORT_DRAFT_PREVIEW_DIALOG, ReportPdfPreview } from './ReportPdfPreview';
import { REPORT_VALIDATION_DIALOG, ReportValidationDialog } from './ReportValidationDialog';

/** "Create New Report" form (Angular TaxreturnAddnewreportComponent), `/home/taxreturns/addnewreport?type=&id=`. */
export function TaxreturnAddnewreport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client] = useState(() => selectedClient());
  // The query is read once, like Angular's snapshot.
  const [initialType] = useState<number>(() =>
    searchParams.get('type') === '1' ? ReportTypeCode.Management : ReportTypeCode.Annual,
  );
  const [reportId, setReportId] = useState<string | null>(() => {
    const id = searchParams.get('id');
    return isGuid(id) ? id : null;
  });
  /** TC 86488: opened from Reports > CIC Reports > Add CIC Report (`&cic=1`). */
  const [isCic] = useState(() => searchParams.get('cic') === '1');
  /** The client's locked accounting periods: no report is offered their trial balances as the current one. */
  const [lockedPeriodIds, setLockedPeriodIds] = useState<ReadonlySet<string>>(() => new Set());
  const [form, setForm] = useState<FormState>(() => initialFormState(initialType));
  const [reportTypes, setReportTypes] = useState<ReportTypeOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  const mounted = useRef(true);
  const v = form.v;
  // BUG-078: a sole trader or partnership has no directors, so no Director Sign Date. Neither sign date is required (3.0
  // Reports/Default.js:139-140): both are pre-filled with today for a new report and may be cleared.
  const clientType = useClientKind(client?.id)?.clientType;
  const noDirectors = isUnincorporated(clientType);
  // A sole trader has a single set of accounts, so there is no report type to choose.
  const isSoleTrader = clientType === ClientKind.SoleTrader && initialType === ReportTypeCode.Annual;
  const isLlp = clientType === ClientKind.Llp;

  /**
   * BUG-062/064: "Accounts Type" - new accounts, or accounts revised by replacement (0) or with a supplementary note (1).
   * It belongs to the accounting period of the chosen trial balance, so it is read when that changes and written with the report.
   */
  // APT356 (client, 30 Oct): "Set New Accounts as the default selection for Account Type" - a new report starts on New Accounts (it used to
  // start with none chosen, as 3.0 did, Oct-10 #169). A revised report is chosen on purpose, never taken over from the period, or a normal
  // report printed the period's supplementary note (Round 3 minor, 85804-85812). A saved report shows its own Accounts Type; one saved
  // before it was kept shows the period's revision, as it prints.
  const [revisionChoice, setRevisionChoice] = useState<'new' | '0' | '1' | null>('new');
  const [savedRevision, setSavedRevision] = useState<string>('new');
  // Round 3 (85804-85812): a saved report keeps its own Accounts Type - New Accounts stays new although the period was revised.
  const reportAccountsType = useRef<'new' | '0' | '1' | null>(null);
  // An Accounts Type the preparer picked is theirs: choosing another trial balance no longer puts it back to the default.
  const revisionChosen = useRef(false);
  const periodId = form.trialBalances.find((t) => t.id === v.currentTrialBalance)?.accountingPeriodId ?? null;
  const showsAccountsType = !noDirectors && (v.reportType ?? initialType) === ReportTypeCode.Annual;

  useEffect(() => {
    if (!client || !periodId || !showsAccountsType) return;
    let cancelled = false;
    reportOptionsService.getRevisedAccounts(client.id, periodId).then(
      (view) => {
        if (cancelled) return;
        const current =
          view.revisionType === null || view.revisionType === undefined ? 'new' : (`${view.revisionType}` as '0' | '1');
        setSavedRevision(current);
        if (!revisionChosen.current) setRevisionChoice(reportAccountsType.current ?? (reportId ? current : 'new'));
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId, showsAccountsType]);

  const setField = <K extends FieldName>(name: K, value: FormValues[K]) =>
    setForm((prev) => setControl(prev, name, value));

  // ---- Loading ----

  useEffect(() => {
    mounted.current = true;
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return () => {
        mounted.current = false;
      };
    }
    let cancelled = false;
    Promise.all([
      reportsService.getReportFormOptions(client.id, initialType),
      reportsService.getTrialBalances(client.id),
      reportId ? reportsService.getReport(reportId) : Promise.resolve(null),
    ]).then(
      ([options, trialBalances, detail]) => {
        if (cancelled) return;
        setReportTypes(withSavedVariant(options.reportTypes, detail));
        const own = detail?.accountsType;
        reportAccountsType.current = own === 2 ? 'new' : own === 0 || own === 1 ? (`${own}` as '0' | '1') : null;
        if (reportAccountsType.current) setRevisionChoice(reportAccountsType.current);
        setForm((prev) =>
          withLoadedData(prev, {
            trialBalances,
            nextRefNo: options.nextRefNo,
            detail,
            adjustedSources: options.adjustedSources,
          }),
        );
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    // TC 86488, for every report type as the old application: the locked periods, whose trial balances are not offered.
    practiceService.getClientPeriods(client.id).then(
      (response) => {
        if (cancelled) return;
        setLockedPeriodIds(new Set((response.periods ?? []).filter((p) => p.isLocked).map((p) => p.id)));
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Actions ----

  /** Cancel Changes: the form as loaded or last saved, its Accounts Type too. */
  const onCancel = () => {
    if (saving.current) return;
    revisionChosen.current = false;
    // A saved report shows its own Accounts Type (or, saved before it was kept, the period's revision); a new report goes
    // back to New Accounts.
    setRevisionChoice(reportAccountsType.current ?? (reportId ? (savedRevision as 'new' | '0' | '1') : 'new'));
    setForm(cancelChanges);
  };

  const onClose = () => {
    const type = form.v.reportType ?? initialType;
    if (isCic) {
      navigate('/home/taxreturns/reports-cicreports');
      return;
    }
    navigate(
      type === ReportTypeCode.Management
        ? '/home/taxreturns/reports-managementaccounts'
        : '/home/taxreturns/reports-accounts',
    );
  };

  /**
   * Angular FormValidationService.validateForm: marks every control touched and toasts when invalid. BUG-048: each
   * invalid control also says under itself what it needs.
   */
  const validateForm = (): boolean => {
    const accountsTypeMissing = showsAccountsType && revisionChoice === null;
    const invalid = isFormInvalid(form) || accountsTypeMissing;
    if (invalid) {
      setForm(touchAll);
      // BUG-048 (QA round 2): name each missing field, as the old application did; Oct-10 #169: the Accounts Type first.
      const fields = missingFieldsMessage(form);
      const message = [accountsTypeMissing ? `${ACCOUNTS_TYPE_MESSAGE}.` : null, fields].filter(Boolean).join(' ');
      notifyError(message || NotificationTextMessage.validationMessage);
    }
    return !invalid;
  };

  /** BUG-048: the report as the form describes it - what the checks and the preview see and what Save Report stores. */
  const payloadOf = (values: FormValues, clientId: string): SaveReportPayload => ({
    clientId,
    id: reportId,
    reportType: values.reportType as number,
    accountsVariant: values.accountsVariant ?? 0,
    // Round 3: this report's Accounts Type, so New Accounts never prints the period's supplementary note.
    accountsType: !showsAccountsType
      ? null
      : revisionChoice === 'new'
        ? 2
        : revisionChoice === '0' || revisionChoice === '1'
          ? Number(revisionChoice)
          : null,
    refNo: (values.refNo ?? '').trim(),
    // BUG-048: optional - a blank description is sent as an empty string (app.Report.Description is NOT NULL).
    description: (values.description ?? '').trim(),
    currentTrialBalanceId: values.currentTrialBalance as string,
    showComparative: !!values.showComparative,
    previousTrialBalanceId: values.showComparative ? values.previousTrialBalance : null,
    directorSignDate: toIsoDate(values.directorSignDate),
    accountantSignDate: toIsoDate(values.accountantSignDate),
    letterOfEngagementDate: toIsoDate(values.letterOfEngagement),
    clientApprovalDate: toIsoDate(values.clientApprovalDate),
    autoCheckNotes: values.relatedNotesCheck !== 'manual',
    // APT342: left out for the trial balance as saved, so a report that does not choose the Adjusted Trial Balance is sent as it always was.
    ...(balanceSourceOf(values) === 1 ? { balanceSource: 1 } : {}),
  });

  /**
   * The Companies House filing rules (and BUG-047's policy and disclosure checks) are run before the accounts are shown:
   * every check - blocking ones included - can be skipped past ("Skip & Preview Report"), as Capium 3.0 does; the hard
   * stops are at Submit. BUG-048: the report is checked as the form describes it, before anything is saved. Resolves false when the
   * preparer stops at the issues (Generate then returns to the list).
   */
  const passesChecks = async (payload: SaveReportPayload): Promise<boolean> => {
    let result;
    try {
      result = await reportsService.validateReportDraft(payload);
    } catch (error) {
      // The server refusing the report (it says why) stops here. The check failing to run at all does not: it is a
      // courtesy check, and the preview checks the report again before showing it.
      if ((error as { error?: { detail?: unknown } })?.error?.detail) {
        throw error;
      }
      return true;
    }
    if (result.checks.length === 0) {
      return true;
    }
    const skip = await openDialog(ReportValidationDialog, {
      data: { checks: result.checks },
      ...REPORT_VALIDATION_DIALOG,
    }).afterClosed();
    // Close means "go and fix it": the preparer stays on the form with their edits.
    return !!skip;
  };

  /**
   * BUG-048 (as the old application): Generate -> validation -> preview -> Save Report. Generate checks the report
   * and shows its accounts without saving it; the preview's Save Report stores it and returns to the list. Closing
   * the preview saves nothing and leaves the form as it was.
   * 5 Nov design review: this used to offer a second "Generate" that stayed on the saved report instead of
   * returning to the list; dropped in favour of the one Generate action, always returning to the list.
   */
  const generate = async () => {
    if (!client || saving.current || !validateForm()) {
      return;
    }
    const values = { ...form.v };
    const payload = payloadOf(values, client.id);
    const choice = revisionChoice;
    const choicePeriodId = periodId;

    saving.current = true;
    setIsSaving(true);
    try {
      // The checks and the preview print the Accounts Type the payload carries (the server applies it to the period's
      // revision), so nothing is written before the report is saved: closing the preview leaves the period as it was.
      // Issue #9 (QA): the Validation status report shows for Manual Check as well as Auto Check. The choice only decides
      // whether the accounting policies are switched on for the preparer (autoCheckNotes in the payload).
      if (!(await passesChecks(payload))) {
        return;
      }
      const detail = await openDialog(ReportPdfPreview, {
        data: { refNo: payload.refNo, draft: payload },
        ...REPORT_DRAFT_PREVIEW_DIALOG,
      }).afterClosed();
      if (!detail || !mounted.current) {
        return;
      }
      setReportId(detail.id);
      setForm((prev) => withSavedReport(prev, detail));
      reportAccountsType.current = payload.accountsType === 2 ? 'new' : choice;
      // BUG-062/064: the revision belongs to the trial balance's accounting period (as Report Settings > Revised
      // Accounts), so a revised report records its revision there - once it is saved.
      if (showsAccountsType && choicePeriodId && (choice === '0' || choice === '1') && choice !== savedRevision) {
        await reportOptionsService.saveRevisionType(client.id, choicePeriodId, Number(choice));
        if (mounted.current) setSavedRevision(choice);
      }
      // The checks shown before the preview are recorded against the saved report, as the list reports them.
      // Awaited, so the list it returns to shows this report's status rather than the one recorded before the edit.
      await reportsService.validateReport(detail.id).catch(() => undefined);
      onClose();
    } catch (error) {
      notifyError(error);
    } finally {
      saving.current = false;
      if (mounted.current) {
        setIsSaving(false);
      }
    }
  };

  /** The current trial balances this form offers, and (APT342) the periods whose Adjusted Trial Balance can be the source instead. */
  const currentTrialBalances = unlockedTrialBalances(
    form.trialBalances,
    lockedPeriodIds,
    form.loaded?.currentTrialBalanceId,
  );
  const offeredTrialBalanceIds = new Set(currentTrialBalances.map((t) => t.id));
  const adjustedChoices = offeredAdjustedSources(form, offeredTrialBalanceIds);
  /** APT342 (client, 28 Oct 10:52): the Source of Figures choice that is on; its period is chosen in the second selector. */
  const usesAdjusted = form.sourceKind === 'adjusted';
  const savedLineage =
    form.loaded?.balanceSource === 1 && form.loaded.sourceAccountingPeriodId === v.adjustedPeriod ? form.loaded : null;

  const invalid = (name: FieldName) => showInvalid(form, name);
  const error = (name: FieldName) => fieldError(form, name);
  const touchField = (name: FieldName) => setForm((prev) => touch(prev, name));

  /**
   * task-104: each column of the notes is headed with its own trial balance's year, so picking a
   * comparative that ends in the same year as the current one prints both columns under that year.
   * The picker offers every trial balance but the one already chosen, so nothing else catches it.
   */
  const sameYearAsCurrent = (() => {
    if (!v.showComparative || !v.currentTrialBalance || !v.previousTrialBalance) return false;
    // toDate arrives as either 31-Mar-2027 or 2027-03-31, so take the four-digit run rather
    // than a fixed position.
    const year = (id: string) => form.trialBalances.find((t) => t.id === id)?.toDate?.match(/\d{4}/)?.[0];
    const current = year(v.currentTrialBalance);
    return !!current && current === year(v.previousTrialBalance);
  })();

  return (
    <SideMenuPage menu="reports">
      <div>
        <div className="sidenav-details add-report-page mb-4">
          <PageHeader title="Create New Report">
            <Button className="action-button primary-button" busy={isSaving} onClick={() => void generate()}>
              Generate
            </Button>
            <Button className="action-button secondary-button" disabled={isSaving} onClick={onCancel}>
              Cancel Changes
            </Button>
            <Button className="close-btn" aria-label="Close" onClick={onClose}>
              <Icon name="close" />
            </Button>
          </PageHeader>
          <div className="mt-1">
            <div className="sidenav-body-content p-0">
              <div className="wp-100">
                {/* Design review 2 Nov 2026: everything on one screen, half left and half right (no Details / Figures / Notes tabs). */}
                <div className="add-report-columns">
                  <section className="add-report-column" aria-label="Details">
                    <div>
                      <div className="form-field">
                        <p>
                          Ref. No.<span className="text-red">*</span>
                        </p>
                        <InputField
                          fieldClassName="w-387"
                          autoComplete="off"
                          // BUG-048: at most 10 characters, as the old application ("AA-1, max 10"). A longer reference
                          // saved before this limit is shown in full and still saves unchanged (the server allows it).
                          maxLength={refNoMaxLength(form)}
                          charMode="alphanumeric"
                          value={v.refNo ?? ''}
                          invalid={invalid('refNo')}
                          onChange={(event) => {
                            setField('refNo', event.target.value);
                          }}
                          onBlur={() => touchField('refNo')}
                        />
                        <FieldError message={error('refNo')} />
                      </div>
                      <div className="form-field textarea">
                        {/* BUG-048: optional, as in the 3.0 app. */}
                        <p>Description</p>
                        <TextareaField
                          fieldClassName="w-387"
                          autoComplete="off"
                          maxLength={500}
                          value={v.description ?? ''}
                          onChange={(event) => {
                            setField('description', event.target.value);
                          }}
                          onBlur={() => touchField('description')}
                        />
                      </div>
                      {!isSoleTrader && (
                        <div className="form-field">
                          <p>
                            Report Type<span className="text-red">*</span>
                          </p>
                          <Select
                            className={`w-387${invalid('accountsVariant') ? ' ng-invalid ng-touched' : ''}`}
                            items={reportTypes}
                            bindLabel="name"
                            bindValue="id"
                            groupBy="group"
                            placeholder="Select Report Type"
                            clearable={false}
                            value={v.accountsVariant}
                            onChange={(value) => {
                              setField('accountsVariant', value as number | null);
                            }}
                            onBlur={() => touchField('accountsVariant')}
                          />
                          <FieldError message={error('accountsVariant')} />
                        </div>
                      )}
                      {/*
                    APT342 (client, 28 Oct 10:52: "just show option to choose between source TB or ATB and let client choose the TB for the
                    period they want"): two choices only; the list below changes with it - the trial balances of the Trial Balance section, or the
                    periods that have an adjusted trial balance. The Adjusted choice is off, with its reason, when no period offers one, and no
                    adjusted trial balance is picked for the user when several do.
                  */}
                      <div className="form-field">
                        <p>Source of Figures</p>
                        <RadioGroup
                          name="sourceKind"
                          value={form.sourceKind}
                          onChange={(value) => {
                            setForm((prev) => chooseFiguresSource(prev, value as SourceKind, offeredTrialBalanceIds));
                          }}
                        >
                          <div className="d-flex align-center gap-15">
                            <Radio value="source" hostClassName="example-radio-button">
                              Source trial balance
                            </Radio>
                            <Radio
                              value="adjusted"
                              hostClassName="example-radio-button"
                              disabled={adjustedChoices.length === 0 && !usesAdjusted}
                            >
                              Adjusted trial balance
                            </Radio>
                          </div>
                        </RadioGroup>
                        {adjustedChoices.length === 0 && !usesAdjusted && (
                          <p className="text-muted font-size-12 mt-1 w-387">
                            No adjusted trial balance is available yet. A period has one once its trial balance is
                            posted.
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                  <section className="add-report-column" aria-label="Figures and notes">
                    <h3 className="add-report-heading">Figures</h3>
                    <div>
                      <div className="form-field">
                        <p>
                          {usesAdjusted ? 'Adjusted Trial Balance' : 'Current Trial Balance'}
                          <span className="text-red">*</span>
                        </p>
                        {usesAdjusted ? (
                          <Select
                            className={`w-387${invalid('currentTrialBalance') ? ' ng-invalid ng-touched' : ''}`}
                            items={adjustedChoices.map((x) => ({ id: x.accountingPeriodId, label: x.label }))}
                            bindLabel="label"
                            bindValue="id"
                            placeholder="Select Adjusted Trial Balance"
                            clearable={false}
                            value={v.adjustedPeriod}
                            onChange={(value) => {
                              setForm((prev) =>
                                touch(chooseAdjustedSource(prev, value as string | null), 'currentTrialBalance'),
                              );
                            }}
                            onBlur={() => touchField('currentTrialBalance')}
                          />
                        ) : (
                          <Select
                            className={`w-387${invalid('currentTrialBalance') ? ' ng-invalid ng-touched' : ''}`}
                            items={currentTrialBalances}
                            bindLabel="label"
                            bindValue="id"
                            placeholder="Select Reference"
                            value={v.currentTrialBalance}
                            onChange={(value) => {
                              setField('currentTrialBalance', value as string | null);
                            }}
                            onBlur={() => touchField('currentTrialBalance')}
                          />
                        )}
                        <FieldError message={error('currentTrialBalance')} />
                        {usesAdjusted && v.adjustedPeriod && (
                          <p className="text-muted font-size-12 mt-1 w-387">
                            The figures are this period’s source trial balance plus its posted journals.
                          </p>
                        )}
                        {savedLineage && usesAdjusted && (
                          <p className="text-muted font-size-12 mt-1 w-387">
                            Generated from the adjusted balances at version {savedLineage.sourceBalanceVersion ?? '-'}
                            {savedLineage.sourceCapturedAtUtc
                              ? `, captured ${dayjs(savedLineage.sourceCapturedAtUtc).format('DD-MMM-YYYY HH:mm')}`
                              : ''}
                            .
                          </p>
                        )}
                        {form.deletedTrialBalanceNote && (
                          <p className="text-red font-size-12 mt-1 w-387">{form.deletedTrialBalanceNote}</p>
                        )}
                        {/* #183: a management accounts report reads the trial balance(s) chosen here, not a period snapshot. */}
                        {(form.v.reportType ?? initialType) === ReportTypeCode.Management && !usesAdjusted && (
                          <p className="text-muted font-size-12 mt-1 w-387">
                            The figures come from the trial balance selected here (and the previous one when comparative
                            figures are shown).
                          </p>
                        )}
                      </div>
                      <div className="form-field">
                        <Toggle
                          checked={!!v.showComparative}
                          onCheckedChange={(on) => {
                            setField('showComparative', on);
                          }}
                        >
                          Show Comparative Figures
                        </Toggle>
                      </div>
                      {/* #158: only asked for when comparative figures are shown. */}
                      {v.showComparative && (
                        <div className="form-field">
                          <p>
                            Previous Trial Balance<span className="text-red">*</span>
                          </p>
                          <Select
                            className={`w-387${invalid('previousTrialBalance') ? ' ng-invalid ng-touched' : ''}`}
                            items={form.previousTrialBalances}
                            bindLabel="label"
                            bindValue="id"
                            placeholder="Select Reference"
                            value={v.previousTrialBalance}
                            onChange={(value) => {
                              setField('previousTrialBalance', value as string | null);
                            }}
                            onBlur={() => touchField('previousTrialBalance')}
                          />
                          <FieldError message={error('previousTrialBalance')} />
                          {/* task-104: the notes head each column with its trial balance's year, so a
                          comparative ending in the same year as the current one prints two columns
                          under the same heading. The list offers every other trial balance, and
                          nothing else checks the periods differ. */}
                          {sameYearAsCurrent && (
                            <p className="font-size-12 text-danger mb-0 mt-5">
                              This trial balance ends in the same year as the current one, so both columns of the
                              accounts would be headed with that year. Choose the prior year’s trial balance.
                            </p>
                          )}
                        </div>
                      )}
                      {showsAccountsType && (
                        <div className="form-field">
                          <p>Accounts Type</p>
                          <RadioGroup
                            value={revisionChoice}
                            onChange={(value) => {
                              revisionChosen.current = true;
                              setRevisionChoice(value as 'new' | '0' | '1');
                            }}
                          >
                            <Radio value="new">New Accounts</Radio>
                            <Radio value="0">Revised - by replacement</Radio>
                            <Radio value="1">Revised - with a supplementary note</Radio>
                          </RadioGroup>
                          {(revisionChoice === '0' || revisionChoice === '1') && (
                            <p className="font-size-12 text-gray mb-0">
                              Enter the revision details on Report Settings &gt; Revised Accounts; the report carries
                              them.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <h3 className="add-report-heading">Notes</h3>
                    <div>
                      <div className="form-field">
                        <p>Related Notes</p>
                        <RadioGroup
                          value={v.relatedNotesCheck}
                          onChange={(value) => {
                            setField('relatedNotesCheck', value as string);
                          }}
                        >
                          <Radio value="auto">Auto Check</Radio>
                          <Radio value="manual">Manual Check</Radio>
                        </RadioGroup>
                        {v.relatedNotesCheck === 'auto' && (
                          <p className="font-size-12 text-gray mb-0">
                            Switches on the accounting policies your trial balance needs and checks the filing rules
                            before the accounts are shown.
                          </p>
                        )}
                        {v.relatedNotesCheck === 'manual' && (
                          <p className="font-size-12 text-gray mb-0">
                            Leaves the notes and policies as you set them. The filing rules are still checked before the
                            accounts are shown.
                          </p>
                        )}
                      </div>
                      <div className="content-box hr-top pt-1 mt-2">
                        <div className="d-flex gap-30">
                          {/* task-162: these dates are read-only so they cannot be half-typed, which left the
                        small calendar icon as the only way to change them - clicking the date itself did
                        nothing, so they read as fixed. Clicking anywhere in the field now opens the calendar. */}
                          {!noDirectors && (
                            <div className="form-field">
                              {/* An LLP's members approve its accounts; the old application labelled the date for them. */}
                              <p title={isLlp ? LLP_DIRECTOR_SIGN_DATE_TITLE : undefined}>
                                {isLlp ? LLP_DIRECTOR_SIGN_DATE_LABEL : 'Director Sign Date'}
                              </p>
                              <DateField
                                fieldClassName="w-180"
                                readOnly
                                openOnInputClick
                                clearable
                                value={v.directorSignDate}
                                invalid={invalid('directorSignDate')}
                                onChange={(value) => {
                                  setField('directorSignDate', value);
                                }}
                              />
                              <FieldError message={error('directorSignDate')} />
                            </div>
                          )}
                          <div className="form-field">
                            {/* Optional, as the old application's rules (Default.js:139-140): it may be cleared. */}
                            <p>Accountant Sign Date</p>
                            <DateField
                              fieldClassName="w-180"
                              readOnly
                              openOnInputClick
                              clearable
                              value={v.accountantSignDate}
                              invalid={invalid('accountantSignDate')}
                              onChange={(value) => {
                                setField('accountantSignDate', value);
                              }}
                            />
                            <FieldError message={error('accountantSignDate')} />
                          </div>
                        </div>
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>Letter of Engagement</p>
                            <DateField
                              fieldClassName="w-180"
                              readOnly
                              openOnInputClick
                              clearable
                              value={v.letterOfEngagement}
                              onChange={(value) => {
                                setField('letterOfEngagement', value);
                              }}
                            />
                          </div>
                          <div className="form-field">
                            <p>Client Approval Date</p>
                            {/* The date is optional and the old application let it be cleared (the certificate then prints no date). */}
                            <DateField
                              fieldClassName="w-180"
                              readOnly
                              openOnInputClick
                              clearable
                              value={v.clientApprovalDate}
                              onChange={(value) => {
                                setField('clientApprovalDate', value);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}

/**
 * The Report Type choices the server offers this client (by its type and Report Settings), with the variant a saved report
 * was generated with added when it is no longer offered, so the report still opens as it was saved (the server keeps it).
 */
function withSavedVariant(options: ReportTypeOption[], detail: ReportDetail | null): ReportTypeOption[] {
  if (!detail || detail.reportType !== ReportTypeCode.Annual || options.some((o) => o.id === detail.accountsVariant)) {
    return options;
  }
  const name = ['Annual Full Accounts', 'Annual Abridged Accounts', 'Annual Filleted Accounts'][detail.accountsVariant];
  return name ? [...options, { id: detail.accountsVariant, name, group: options[0]?.group ?? null }] : options;
}

/** BUG-048: the message under an invalid control. */
function FieldError({ message }: { message: string | null }) {
  return message ? (
    <p className="field-error text-red font-size-12 mt-1 mb-0 w-387" role="alert">
      {message}
    </p>
  ) : null;
}
